import type { AgentConfig, Avatar as AvatarValue, CreateAgentRequest, RosterAgent } from "@ark-bot/protocol";
import { useState } from "react";

import { useBackClose } from "../back.js";
import { useText } from "../i18n.js";
import { matchesQuery } from "../roster.js";
import { NEW_CHAT } from "../text/sidebar.js";

import { Avatar } from "./Avatar.js";
import { AvatarField } from "./AvatarPicker.js";

/** As many members as the box will run in one room. */
export const MAX_MEMBERS = 6;

/** The panel's pages, in the order WhatsApp walks them: the menu, then a form, or the member picker and then the form. */
type Step = "menu" | "agent" | "members" | "group";

type Words = (typeof NEW_CHAT)["en"];

const TITLE: Record<Step, (t: Words) => string> = { menu: (t) => t.titleMenu, agent: (t) => t.titleAgent, members: (t) => t.titleMembers, group: (t) => t.titleGroup };

interface Props {
  readonly agents: readonly RosterAgent[];
  readonly onCreate: (request: CreateAgentRequest) => Promise<void>;
  /** Picking an existing agent from the list under the menu opens its chat. */
  readonly onOpen: (id: string) => void;
  readonly onClose: () => void;
  readonly closing?: boolean;
}

function configFrom(title: string, description: string, avatar: AvatarValue | undefined): AgentConfig {
  const config: AgentConfig = {};
  if (title.trim().length > 0) config.title = title.trim();
  if (description.trim().length > 0) config.description = description.trim();
  if (avatar != null) config.avatar = avatar;
  return config;
}

const BackIcon = (): React.JSX.Element => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <path d="M12.5 4 6.5 10l6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NextIcon = (): React.JSX.Element => (
  <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
    <path d="M4 10h11M10.5 5.5 15 10l-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Making an agent or a group, the way WhatsApp starts a chat: the "+" slides
 * this over the roster with "New agent" and "New group" on top and the
 * agents beneath, as its contacts; a group is a chat, not a contact, so
 * none is listed. A group picks its members first, as chips over the list,
 * and names itself after. The new agent's face is set right here, before it
 * exists, so the request carries it.
 */
export function NewChat({ agents, onCreate, onOpen, onClose, closing = false }: Props): React.JSX.Element {
  const t = useText(NEW_CHAT);
  const [step, setStep] = useState<Step>("menu");
  const [query, setQuery] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [avatar, setAvatar] = useState<AvatarValue | undefined>(undefined);
  const [members, setMembers] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const candidates = agents.filter((agent) => agent.kind !== "group");
  const named = (id: string): RosterAgent | undefined => agents.find((agent) => agent.id === id);

  const go = (next: Step): void => {
    setStep(next);
    setQuery("");
    setError(null);
  };
  // Back walks the pages the way the arrow does: the form to the members, the members or the agent form to the menu.
  useBackClose(step !== "menu", () => go(step === "group" ? "members" : "menu"));

  const toggle = (id: string): void => {
    setMembers((current) => (current.includes(id) ? current.filter((member) => member !== id) : current.length >= MAX_MEMBERS ? current : [...current, id]));
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const trimmed = name.trim();
    if (creating || trimmed.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const config = configFrom(step === "group" ? "" : title, description, avatar);
      await onCreate(step === "group" ? { name: trimmed, kind: "group", members, config } : { name: trimmed, config, introduce: true });
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setCreating(false);
    }
  };

  const list = (rows: readonly RosterAgent[], onPick: (agent: RosterAgent) => void, chosen?: ReadonlySet<string>): React.JSX.Element => (
    <ul className="pick-list">
      {rows.map((agent) => (
        <li key={agent.id}>
          <button type="button" className={`pick-row${chosen?.has(agent.id) === true ? " chosen" : ""}`} onClick={() => onPick(agent)} aria-pressed={chosen == null ? undefined : chosen.has(agent.id)} disabled={chosen != null && !chosen.has(agent.id) && chosen.size >= MAX_MEMBERS}>
            <Avatar id={agent.id} name={agent.name} avatar={agent.config.avatar} size={38} />
            <span className="pick-body">
              <strong>{agent.name}</strong>
              {agent.config.title != null && agent.config.title.trim().length > 0 ? <span className="muted">{agent.config.title}</span> : null}
            </span>
            {chosen?.has(agent.id) === true ? <span className="pick-check">✓</span> : null}
          </button>
        </li>
      ))}
      {rows.length === 0 ? <li className="hint">{query.trim().length > 0 ? t.nothingMatches(query) : t.noBots}</li> : null}
    </ul>
  );

  const searchBox = (placeholder: string): React.JSX.Element => (
    <label className="search">
      <span className="search-icon" aria-hidden="true">
        ⌕
      </span>
      <input type="search" autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={placeholder} aria-label={placeholder} />
    </label>
  );

  const form = (): React.JSX.Element => (
    <form className="agent-settings profile-form new-chat-form" onSubmit={(event) => void submit(event)}>
      <AvatarField id="new" name={name.trim().length > 0 ? name : step === "group" ? t.newGroup : t.newBot} value={avatar} onChange={setAvatar} />
      <label>
        {step === "group" ? t.groupName : t.name}
        <input autoFocus value={name} onChange={(event) => setName(event.target.value)} required placeholder={step === "group" ? t.groupExample : "Exusiai"} />
      </label>
      {step === "group" ? null : (
        <label>
          <span>
            {t.title} <span className="muted">{t.optional}</span>
          </span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t.titleExample} />
        </label>
      )}
      <label>
        {t.description}
        <textarea rows={5} value={description} onChange={(event) => setDescription(event.target.value)} placeholder={step === "group" ? t.groupDescriptionExample : t.botDescriptionExample} />
      </label>
      {step === "group" ? (
        <div className="chips" aria-label={t.members}>
          {members.map((id) => {
            const member = named(id);
            return member == null ? null : (
              <span key={id} className="chip">
                <Avatar id={member.id} name={member.name} avatar={member.config.avatar} size={22} />
                {member.name}
              </span>
            );
          })}
          <button type="button" className="linkish" onClick={() => go("members")}>
            {t.edit}
          </button>
        </div>
      ) : null}
      {error != null ? <div className="settings-error">{error}</div> : null}
      <div className="settings-actions">
        <button type="submit" className="primary" disabled={creating || name.trim().length === 0}>
          {creating ? t.creating : t.create}
        </button>
      </div>
    </form>
  );

  return (
    <div className={`sidebar-panel${closing ? " closing" : ""}`} role="dialog" aria-label={TITLE[step](t)}>
      <header className="panel-head">
        <button type="button" className="icon" onClick={() => (step === "menu" ? onClose() : step === "group" ? go("members") : go("menu"))} aria-label={step === "menu" ? t.close : t.back} title={step === "menu" ? t.close : t.back}>
          <BackIcon />
        </button>
        <span className="panel-title">{TITLE[step](t)}</span>
      </header>

      {step === "menu" ? (
        <>
          {searchBox(t.searchBots)}
          <div className="panel-body">
            <ul className="pick-list menu-list">
              <li>
                <button type="button" className="pick-row" onClick={() => go("agent")}>
                  <span className="pick-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <circle cx="9" cy="7" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M3.5 16.5c.6-3 3-4.5 5.5-4.5s4.9 1.5 5.5 4.5M15.5 6v4M13.5 8h4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="pick-body">
                    <strong>{t.newBot}</strong>
                  </span>
                </button>
              </li>
              <li>
                <button type="button" className="pick-row" onClick={() => go("members")} disabled={candidates.length === 0} title={candidates.length === 0 ? t.groupNeedsBots : t.groupAbout}>
                  <span className="pick-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <circle cx="7" cy="7.5" r="2.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
                      <circle cx="13.5" cy="8" r="2.3" fill="none" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M2.5 16c.5-2.6 2.4-4 4.5-4s4 1.4 4.5 4M12 15.5c.4-1.8 1.7-3 3.5-3 1 0 1.8.3 2.5.9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="pick-body">
                    <strong>{t.newGroup}</strong>
                  </span>
                </button>
              </li>
            </ul>
            <div className="section-head">
              <span className="section-name">{t.bots}</span>
            </div>
            {list(
              candidates.filter((agent) => matchesQuery(agent, query)).sort((a, b) => a.name.localeCompare(b.name)),
              (agent) => {
                onOpen(agent.id);
                onClose();
              },
            )}
          </div>
        </>
      ) : null}

      {step === "members" ? (
        <>
          <div className="chips-field">
            {members.map((id) => {
              const member = named(id);
              return member == null ? null : (
                <span key={id} className="chip">
                  <Avatar id={member.id} name={member.name} avatar={member.config.avatar} size={22} />
                  {member.name}
                  <button type="button" className="chip-remove" onClick={() => toggle(id)} aria-label={t.remove(member.name)}>
                    ×
                  </button>
                </span>
              );
            })}
            <input type="search" autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={members.length === 0 ? t.searchBots : ""} aria-label={t.searchBots} />
          </div>
          <div className="panel-body">
            <div className="section-head">
              <span className="section-name">{t.bots}</span>
              <span className="section-count muted">
                {members.length}/{MAX_MEMBERS}
              </span>
            </div>
            {list(
              candidates.filter((agent) => matchesQuery(agent, query)).sort((a, b) => a.name.localeCompare(b.name)),
              (agent) => toggle(agent.id),
              new Set(members),
            )}
          </div>
          <button type="button" className="panel-next primary" onClick={() => go("group")} disabled={members.length === 0} aria-label={t.next} title={members.length === 0 ? t.pickMember : t.nameGroup}>
            <NextIcon />
          </button>
        </>
      ) : null}

      {step === "agent" || step === "group" ? <div className="panel-body">{form()}</div> : null}
    </div>
  );
}
